import React, { useState, useEffect } from 'react';
import { motion, useSpring, useTransform } from 'framer-motion';
import { Check, Zap, Crown, Users, BarChart3, Shield, Headphones, ArrowRight, Sparkles, MessageSquare, Moon, Sun } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { Link } from 'react-router-dom';

// Smoothly counts the price up/down when plan/billing/slider changes
const AnimatedNumber = ({ value }) => {
  const spring = useSpring(Number(value) || 0, { stiffness: 120, damping: 20, mass: 0.6 });
  const display = useTransform(spring, (v) => Math.round(v).toLocaleString());
  useEffect(() => { spring.set(Number(value) || 0); }, [spring, value]);
  return <motion.span>{display}</motion.span>;
};

const PricingPage = ({ darkMode, toggleDarkMode }) => {
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [growthAccounts, setGrowthAccounts] = useState(5);
  const [isLoading, setIsLoading] = useState(false);
  const [user, setUser] = useState(null);
  const [stripePrices, setStripePrices] = useState(null);
  const [pricesLoading, setPricesLoading] = useState(true);
  const [canUseTrial, setCanUseTrial] = useState(false);

  // Check for trial parameters
  const urlParams = new URLSearchParams(window.location.search);
  const fromTrial = urlParams.get('trial') === 'true';
  const isWelcome = urlParams.get('welcome') === 'true';

  // Pricing configuration based on tiered structure
  const getGrowthPrice = (accounts) => {
    let pricePerAccount;
    if (accounts <= 2) {
      pricePerAccount = 25;
    } else if (accounts <= 5) {
      pricePerAccount = 24;
    } else if (accounts <= 7) {
      pricePerAccount = 23;
    } else if (accounts <= 10) {
      pricePerAccount = 22;
    } else if (accounts <= 15) {
      pricePerAccount = 21;
    } else {
      pricePerAccount = 20;
    }
    
    const monthly = accounts * pricePerAccount;
    const yearly = Math.round(monthly * 10 * 0.8); // 20% off + 2 months free (pay for 10 months)
    
    return { monthly, yearly, pricePerAccount };
  };

  // Stripe Product IDs — prefer the IDs returned by get-stripe-prices (env-aware:
  // resolves to test products in test mode, live in live), fall back to live IDs.
  const productIds = {
    starter: stripePrices?.starter?.product_id || 'prod_SsfjCtoYxu93YS',
    growth: stripePrices?.growth?.product_id || 'prod_SsfqZ9FQppPVuw',
    scale: stripePrices?.scale?.product_id || 'prod_SsfwvSopsBhnL0'
  };

  useEffect(() => {
    // Get current user
    const getCurrentUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      setUser(user);
      
      // Check trial eligibility if user is logged in and came from trial
      if (user && fromTrial) {
        const { data: profile } = await supabase
          .from('profiles')
          .select('has_used_trial')
          .eq('id', user.id)
          .single();
        
        setCanUseTrial(!profile?.has_used_trial);
      }
    };
    getCurrentUser();

    // Fetch real Stripe prices
    const fetchStripePrices = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const response = await fetch(`${supabase.supabaseUrl}/functions/v1/get-stripe-prices`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${session?.access_token}`,
            'apikey': supabase.supabaseKey
          }
        });
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const result = await response.json();
        setStripePrices(result.data);
      } catch (error) {
        console.error('Error fetching Stripe prices:', error);
      } finally {
        setPricesLoading(false);
      }
    };
    fetchStripePrices();
  }, []);

  // Calculate pricing for Growth tier
  const getGrowthPricing = () => {
    const pricing = getGrowthPrice(growthAccounts);
    const starterTotal = 27 * growthAccounts; // What it would cost at Starter rate
    const savings = starterTotal - pricing.monthly;
    
    return {
      monthly: pricing.monthly,
      yearly: pricing.yearly,
      perAccount: pricing.pricePerAccount,
      savings: savings > 0 ? savings : 0
    };
  };

  // Calculate discount percentage
  const getDiscountPercentage = () => {
    const growthPrice = getGrowthPricing();
    const regularPrice = 27 * growthAccounts;
    return Math.round(((regularPrice - growthPrice.monthly) / regularPrice) * 100);
  };

  // Handle subscription creation
  const handleSubscribe = async (tier, accounts = 1) => {
    if (!user) {
      // Redirect to login
      window.location.href = '/login';
      return;
    }

    setIsLoading(true);
    
    try {
      let productId, quantity;
      
      switch (tier) {
        case 'starter':
          productId = productIds.starter;
          quantity = 1;
          break;
        case 'growth':
          productId = productIds.growth;
          quantity = accounts;
          break;
        case 'scale':
          productId = productIds.scale;
          quantity = 1;
          break;
        default:
          throw new Error('Invalid tier');
      }

      const { data, error } = await supabase.functions.invoke('create-checkout-session', {
        body: {
          productId,
          quantity,
          tier,
          billingCycle,
          includeTrial: canUseTrial, // Pass trial flag
          successUrl: `${window.location.origin}/dashboard?welcome=true`,
          cancelUrl: `${window.location.origin}/pricing`
        }
      });

      if (error) throw error;

      // Redirect to Stripe Checkout
      window.location.href = data.url;
      
    } catch (error) {
      console.error('Error creating checkout session:', error);
      alert('Failed to create checkout session. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Calculate tiered pricing (graduated or volume) for Growth tier
  const calculateTieredPrice = (quantity, tiers, tiersMode = 'graduated') => {
    if (!tiers || tiers.length === 0) {
      return getGrowthPricing(); // Fallback to hardcoded logic
    }

    let totalCost = 0;

    if (tiersMode === 'volume') {
      // Volume pricing: find the tier and apply that rate to all units
      const applicableTier = tiers.find(tier => !tier.up_to || quantity <= tier.up_to);
      if (applicableTier) {
        totalCost = quantity * applicableTier.unit_amount;
        if (applicableTier.flat_amount) {
          totalCost += applicableTier.flat_amount;
        }
      }
    } else {
      // Graduated pricing: apply different rates to different ranges
      let remainingQuantity = quantity;
      let currentPosition = 0;

      for (const tier of tiers) {
        if (remainingQuantity <= 0) break;

        const tierStart = currentPosition;
        const tierEnd = tier.up_to || (currentPosition + remainingQuantity);
        const quantityForThisTier = Math.min(remainingQuantity, tierEnd - tierStart);
        
        if (tier.unit_amount && quantityForThisTier > 0) {
          totalCost += quantityForThisTier * tier.unit_amount;
        }
        if (tier.flat_amount) {
          totalCost += tier.flat_amount;
        }

        remainingQuantity -= quantityForThisTier;
        currentPosition = tierEnd;
      }
    }

    return {
      monthly: totalCost,
      yearly: totalCost, // Don't modify here, will be handled separately
      perAccount: totalCost / quantity,
      savings: 0 // Calculate savings vs starter rate if needed
    };
  };

  // Get real prices or fallback to defaults
  const getRealPrice = (tier) => {
    if (!stripePrices || !stripePrices[tier]) {
      // Fallback prices if Stripe data isn't loaded
      const fallbacks = {
        starter: { monthly: 27, yearly: Math.round(27 * 10 * 0.8) },
        growth: getGrowthPricing(),
        scale: { monthly: 397, yearly: Math.round(397 * 10 * 0.8) }
      };
      return fallbacks[tier];
    }

    const tierData = stripePrices[tier];
    
    // Handle Growth tier with tiered pricing
    if (tier === 'growth') {
      const monthlyPrice = tierData.prices.month;
      const yearlyPrice = tierData.prices.year;
      
      let monthlyTotal = 0;
      let yearlyTotal = 0;

      // Calculate monthly pricing
      if (monthlyPrice && monthlyPrice.pricing_type === 'tiered' && monthlyPrice.tiers) {
        const monthlyResult = calculateTieredPrice(growthAccounts, monthlyPrice.tiers, monthlyPrice.tiers_mode);
        monthlyTotal = monthlyResult.monthly;
      }

      // Calculate yearly pricing
      if (yearlyPrice && yearlyPrice.pricing_type === 'tiered' && yearlyPrice.tiers) {
        const yearlyResult = calculateTieredPrice(growthAccounts, yearlyPrice.tiers, yearlyPrice.tiers_mode);
        yearlyTotal = yearlyResult.yearly;
      }

      // If we have valid pricing, return it
      if (monthlyTotal > 0 || yearlyTotal > 0) {
        // Calculate savings vs Starter pricing
        const starterMonthlyRate = stripePrices?.starter?.prices?.month?.amount || 27;
        const starterTotalCost = growthAccounts * starterMonthlyRate;
        const savings = starterTotalCost - monthlyTotal;
        const savingsPercentage = starterTotalCost > 0 ? Math.round((savings / starterTotalCost) * 100) : 0;

        return {
          monthly: monthlyTotal,
          yearly: yearlyTotal,
          perAccount: monthlyTotal / growthAccounts,
          savings: savings,
          savingsPercentage: savingsPercentage
        };
      }

      // Fallback to hardcoded logic if no valid tiers found
      return getGrowthPricing();
    }

    // Handle simple flat pricing for other tiers
    return {
      monthly: tierData.prices.month?.amount || 0,
      yearly: tierData.prices.year?.amount || 0
    };
  };

  const plans = [
    {
      name: 'Starter',
      tier: 'starter',
      icon: Zap,
      color: 'blue',
      price: getRealPrice('starter'),
      accounts: 1,
      description: 'Perfect for testing Instagram outreach',
      features: [
        '1 Instagram account in campaigns',
        'Up to 1,000 DMs per month',
        'Basic message templates',
        'Campaign analytics',
        'Email support'
      ],
      cta: 'Start Free Trial',
      popular: false
    },
    {
      name: 'Growth',
      tier: 'growth',
      icon: BarChart3,
      color: 'cyan',
      price: getRealPrice('growth'),
      accounts: growthAccounts,
      description: 'Scale your outreach with multiple accounts',
      features: [
        `${growthAccounts} Instagram accounts in campaigns`,
        `Up to ${growthAccounts * 1000} DMs per month`,
        'A/B testing & optimization',
        'Advanced analytics & insights',
        'Message sequences',
        'Priority email support'
      ],
      cta: 'Get Started',
      popular: true,
      savings: getRealPrice('growth').savings || 0
    },
    {
      name: 'Scale',
      tier: 'scale',
      icon: Crown,
      color: 'purple',
      price: getRealPrice('scale'),
      accounts: '∞',
      description: 'Enterprise-grade Instagram automation',
      features: [
        'Unlimited Instagram accounts',
        'Unlimited DMs per month',
        'Advanced A/B testing',
        'Custom integrations',
        'Dedicated success manager',
        'Phone & chat support',
        'API access'
      ],
      cta: 'Contact Sales',
      popular: false
    }
  ];

  return (
    <div className={`min-h-screen overflow-x-hidden ${darkMode ? 'bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900' : 'bg-gradient-to-br from-blue-50 via-white to-purple-50'}`}>
      {/* Header */}
      <header className={`relative z-50 ${darkMode ? 'bg-slate-900/80 border-slate-700/50' : 'bg-white/80 border-gray-200/50'} backdrop-blur-md border-b`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-2">
              <div className={`w-8 h-8 ${darkMode ? 'bg-gradient-to-r from-cyan-400 to-blue-500' : 'bg-gradient-to-r from-blue-500 to-purple-600'} rounded flex items-center justify-center`}>
                <MessageSquare className="w-5 h-5 text-white" />
              </div>
              <Link to="/" className={`text-2xl font-bold ${darkMode ? 'bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent' : 'bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent'}`}>
                ICEDMS
              </Link>
            </div>
            <div className="flex items-center space-x-4">
              <nav className="hidden md:flex items-center space-x-6">
                <Link 
                  to="/" 
                  className={`${darkMode ? 'text-slate-300 hover:text-white' : 'text-gray-600 hover:text-gray-900'} font-medium transition-colors`}
                >
                  Home
                </Link>
                <Link 
                  to="/pricing" 
                  className={`${darkMode ? 'text-cyan-400' : 'text-blue-600'} font-medium`}
                >
                  Pricing
                </Link>
                <Link 
                  to="/login" 
                  className={`${darkMode ? 'text-slate-300 hover:text-white' : 'text-gray-600 hover:text-gray-900'} font-medium transition-colors`}
                >
                  Login
                </Link>
              </nav>
              <button 
                onClick={toggleDarkMode}
                className={`p-2 rounded transition-colors ${
                  darkMode 
                    ? 'bg-slate-800 hover:bg-slate-700 text-yellow-400' 
                    : 'bg-gray-100 hover:bg-gray-200 text-gray-600'
                }`}
                aria-label="Toggle dark mode"
              >
                {darkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </button>
              <Link 
                to="/signup"
                className={`px-4 py-2 rounded font-medium transition-colors ${
                  darkMode 
                    ? 'bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white' 
                    : 'bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white'
                } shadow-lg`}
              >
                Get Started
              </Link>
            </div>
          </div>
        </div>
      </header>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="p-4 lg:p-6 max-w-7xl mx-auto pt-16"
      >
      {/* Header */}
      <div className="relative isolate text-center mb-12">
        {/* Animated gradient glow behind the title */}
        <motion.div
          aria-hidden="true"
          initial={{ opacity: 0.35, scale: 0.9 }}
          animate={{ opacity: [0.35, 0.6, 0.35], scale: [0.9, 1.05, 0.9] }}
          transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
          className="pointer-events-none absolute left-1/2 -top-10 -translate-x-1/2 -z-10 w-[480px] max-w-[90vw] h-48 rounded-full blur-3xl bg-gradient-to-r from-cyan-500/30 via-blue-500/30 to-purple-500/30"
        />
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className={`text-4xl lg:text-5xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'} mb-4`}
        >
          Simple, Competitive Pricing
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className={`text-xl ${darkMode ? 'text-slate-300' : 'text-gray-600'} mb-8`}
        >
          30-40% below competitors, with no hidden fees
        </motion.p>

        {/* Trial Welcome Banner */}
        {fromTrial && isWelcome && canUseTrial && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
            className={`${darkMode ? 'bg-green-900/30 border-green-700' : 'bg-green-100 border-green-300'} border rounded p-4 mb-8 text-center`}
          >
            <p className={`${darkMode ? 'text-green-300' : 'text-green-800'} font-semibold`}>
              🎉 Welcome! Choose your plan to start your 3-day free trial
            </p>
            <p className={`${darkMode ? 'text-green-400' : 'text-green-700'} text-sm mt-1`}>
              You won't be charged until your trial ends
            </p>
          </motion.div>
        )}

        {/* Billing Toggle */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="inline-flex items-center space-x-3 p-1 rounded bg-gray-100 dark:bg-slate-800"
        >
          <button
            onClick={() => setBillingCycle('monthly')}
            className={`px-4 py-2 rounded font-medium transition-all ${
              billingCycle === 'monthly'
                ? darkMode 
                  ? 'bg-cyan-500 text-white shadow-lg' 
                  : 'bg-blue-500 text-white shadow-lg'
                : darkMode ? 'text-slate-300' : 'text-gray-600'
            }`}
          >
            Monthly
          </button>
          <button
            onClick={() => setBillingCycle('yearly')}
            className={`px-4 py-2 rounded font-medium transition-all flex items-center space-x-2 ${
              billingCycle === 'yearly'
                ? darkMode 
                  ? 'bg-cyan-500 text-white shadow-lg' 
                  : 'bg-blue-500 text-white shadow-lg'
                : darkMode ? 'text-slate-300' : 'text-gray-600'
            }`}
          >
            <span>Yearly</span>
            <span className="text-xs bg-green-500 text-white px-2 py-1 rounded-full">
              20% off + 2 months free
            </span>
          </button>
        </motion.div>
      </div>


      {/* Pricing Cards */}
      {pricesLoading ? (
        <div className="flex justify-center items-center py-16">
          <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {plans.map((plan, index) => {
          const Icon = plan.icon;
          const isGrowth = plan.tier === 'growth';
          const currentPrice = billingCycle === 'yearly' ? plan.price.yearly : plan.price.monthly;
          
          return (
            <motion.div
              key={plan.tier}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.5, delay: 0.08 * index, ease: [0.21, 0.47, 0.32, 0.98] }}
              whileHover={{ y: -10, transition: { type: 'spring', stiffness: 300, damping: 22 } }}
              className={`relative rounded p-8 transition-shadow duration-300 hover:shadow-2xl ${
                plan.popular
                  ? darkMode
                    ? 'bg-slate-800 border-2 border-cyan-500 shadow-xl shadow-cyan-500/25'
                    : 'bg-white border-2 border-blue-500 shadow-xl shadow-blue-500/25'
                  : darkMode
                    ? 'bg-slate-800 border border-slate-700 shadow-lg'
                    : 'bg-white border border-gray-200 shadow-lg'
              }`}
            >
              {/* Popular Badge */}
              {plan.popular && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.6, y: -8 }}
                  whileInView={{ opacity: 1, scale: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.35, type: 'spring', stiffness: 420, damping: 16 }}
                  className={`absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full text-sm font-medium ${
                  darkMode
                    ? 'bg-cyan-500 text-white'
                    : 'bg-blue-500 text-white'
                } shadow-lg`}>
                  Most Popular
                </motion.div>
              )}

              {/* Plan Header */}
              <div className="text-center mb-6">
                <div className={`inline-flex items-center justify-center w-12 h-12 rounded mb-4 ${
                  plan.color === 'blue' 
                    ? 'bg-blue-100 dark:bg-blue-500/20'
                    : plan.color === 'cyan'
                      ? 'bg-cyan-100 dark:bg-cyan-500/20'
                      : 'bg-purple-100 dark:bg-purple-500/20'
                }`}>
                  <Icon className={`w-6 h-6 ${
                    plan.color === 'blue'
                      ? 'text-blue-600 dark:text-blue-400'
                      : plan.color === 'cyan'
                        ? 'text-cyan-600 dark:text-cyan-400'
                        : 'text-purple-600 dark:text-purple-400'
                  }`} />
                </div>
                
                <h3 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'} mb-2`}>
                  {plan.name}
                </h3>
                
                <p className={`${darkMode ? 'text-slate-400' : 'text-gray-600'} mb-4`}>
                  {plan.description}
                </p>

                {/* Price */}
                <div className="mb-2">
                  <span className={`text-4xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    $<AnimatedNumber value={currentPrice} />
                  </span>
                  <span className={`text-lg ${darkMode ? 'text-slate-400' : 'text-gray-600'}`}>
                    /{billingCycle === 'yearly' ? 'year' : 'month'}
                  </span>
                </div>

                {/* Savings Badge for Growth */}
                {isGrowth && (plan.savings > 0 || plan.price.savingsPercentage > 0) && (
                  <div className="inline-flex items-center space-x-1 text-green-600 text-sm font-medium mb-4">
                    <Sparkles className="w-4 h-4" />
                    <span>
                      {plan.price.savingsPercentage > 0 
                        ? `Save ${plan.price.savingsPercentage}% vs Starter`
                        : `Save $${plan.savings}/month`
                      }
                    </span>
                  </div>
                )}
              </div>

              {/* Features */}
              <ul className="space-y-3 mb-6">
                {plan.features.map((feature, featureIndex) => (
                  <li key={featureIndex} className="flex items-start space-x-3">
                    <Check className={`w-5 h-5 mt-0.5 flex-shrink-0 ${
                      plan.color === 'blue'
                        ? 'text-blue-600 dark:text-blue-400'
                        : plan.color === 'cyan'
                          ? 'text-cyan-600 dark:text-cyan-400'
                          : 'text-purple-600 dark:text-purple-400'
                    }`} />
                    <span className={`${darkMode ? 'text-slate-300' : 'text-gray-700'}`}>
                      {feature}
                    </span>
                  </li>
                ))}
              </ul>

              {/* Growth Tier Account Slider */}
              {isGrowth && (
                <div className={`mb-6 p-4 rounded ${darkMode ? 'bg-slate-700/50' : 'bg-gray-50'}`}>
                  <h4 className={`text-sm font-semibold ${darkMode ? 'text-white' : 'text-gray-900'} mb-3 text-center`}>
                    Choose Your Scale
                  </h4>
                  
                  {/* Account Slider */}
                  <div className="mb-3">
                    <div className="flex justify-between items-center mb-2">
                      <span className={`text-sm ${darkMode ? 'text-slate-300' : 'text-gray-600'}`}>
                        Accounts
                      </span>
                      <span className={`text-lg font-bold ${darkMode ? 'text-cyan-400' : 'text-blue-600'}`}>
                        {growthAccounts}
                      </span>
                    </div>
                    <input
                      type="range"
                      min="2"
                      max="15"
                      value={growthAccounts}
                      onChange={(e) => setGrowthAccounts(parseInt(e.target.value))}
                      className="w-full h-2 bg-gray-200 dark:bg-slate-600 rounded appearance-none cursor-pointer slider"
                      style={{
                        background: `linear-gradient(to right, ${darkMode ? '#06b6d4' : '#3b82f6'} 0%, ${darkMode ? '#06b6d4' : '#3b82f6'} ${((growthAccounts - 2) / 13) * 100}%, ${darkMode ? '#374151' : '#e5e7eb'} ${((growthAccounts - 2) / 13) * 100}%, ${darkMode ? '#374151' : '#e5e7eb'} 100%)`
                      }}
                    />
                    <div className="flex justify-between text-xs text-gray-400 mt-1">
                      <span>2</span>
                      <span>8</span>
                      <span>15</span>
                    </div>
                  </div>

                  {/* Pricing Display */}
                  <div className="text-center">
                    <div className={`text-sm ${darkMode ? 'text-slate-400' : 'text-gray-600'} mb-1`}>
                      ${getGrowthPricing().perAccount?.toFixed(2)} per account
                    </div>
                    {getRealPrice('growth').savingsPercentage > 0 && (
                      <div className="inline-flex items-center space-x-1 text-green-600 text-xs font-medium">
                        <Sparkles className="w-3 h-3" />
                        <span>Save {getRealPrice('growth').savingsPercentage}% vs Starter</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* CTA Button */}
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.97 }}
                onClick={() => handleSubscribe(plan.tier, isGrowth ? growthAccounts : 1)}
                disabled={isLoading}
                className={`w-full py-3 px-6 rounded font-medium transition-all duration-200 flex items-center justify-center space-x-2 ${
                  plan.popular
                    ? darkMode
                      ? 'bg-cyan-500 hover:bg-cyan-600 text-white shadow-lg hover:shadow-xl'
                      : 'bg-blue-500 hover:bg-blue-600 text-white shadow-lg hover:shadow-xl'
                    : darkMode
                      ? 'bg-slate-700 hover:bg-slate-600 text-white border border-slate-600'
                      : 'bg-gray-50 hover:bg-gray-100 text-gray-900 border border-gray-200'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                {isLoading ? (
                  <div className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    <span>{canUseTrial ? 'Start 3-Day Free Trial' : plan.cta}</span>
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </motion.button>

              {/* Account Limit Info */}
              <div className={`mt-4 text-center text-sm ${darkMode ? 'text-slate-400' : 'text-gray-600'}`}>
                {typeof plan.accounts === 'number' 
                  ? `${plan.accounts} account${plan.accounts > 1 ? 's' : ''} in campaigns`
                  : 'Unlimited accounts in campaigns'
                }
              </div>
            </motion.div>
          );
        })}
      </div>
      )}

      {/* FAQ Section */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.2 }}
        transition={{ duration: 0.4 }}
        className="mt-16 text-center"
      >
        <h2 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'} mb-8`}>
          Frequently Asked Questions
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {[
            {
              q: "Can I connect unlimited Instagram accounts?",
              a: "Yes! You can connect as many Instagram accounts as you want. Limits only apply to accounts actively used in campaigns."
            },
            {
              q: "What happens if I exceed my account limit?",
              a: "You'll be prompted to upgrade your plan or increase your Growth plan quantity before adding more accounts to campaigns."
            },
            {
              q: "Can I change plans anytime?",
              a: "Absolutely! You can upgrade, downgrade, or cancel your subscription at any time through your billing portal."
            },
            {
              q: "Is there a free trial?",
              a: "Yes! All plans come with a 3-day free trial so you can test our platform risk-free."
            }
          ].map((faq, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.4, delay: index * 0.12, ease: [0.21, 0.47, 0.32, 0.98] }}
              className={`p-6 rounded ${
                darkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'
              } border text-left`}
            >
              <h3 className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'} mb-2`}>
                {faq.q}
              </h3>
              <p className={`${darkMode ? 'text-slate-400' : 'text-gray-600'}`}>
                {faq.a}
              </p>
            </motion.div>
          ))}
        </div>
      </motion.div>
      </motion.div>
    </div>
  );
};

export default PricingPage;